import { useEffect, useRef } from 'react';

interface AiPopoverProps {
  title: string;
  busy: boolean;
  error: string | null;
  result: string | null;
  onAccept?: (text: string) => void;
  onClose: () => void;
  onRetry: () => void;
}

export function AiPopover({
  title,
  busy,
  error,
  result,
  onAccept,
  onClose,
  onRetry,
}: AiPopoverProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="absolute right-0 top-full z-20 mt-2 w-96 rounded-lg border bg-canvas p-3"
      style={{ boxShadow: '0 10px 30px rgba(0,0,0,.10), 0 0 0 1px rgba(0,0,0,.04)' }}
    >
      <div className="flex items-center justify-between text-[11px]">
        <span className="font-semibold uppercase tracking-wider text-text-muted">{title}</span>
        <button
          onClick={onClose}
          className="text-text-subtle hover:text-[var(--color-fg)]"
          title="Close (esc)"
        >
          ✕
        </button>
      </div>
      <div className="mt-2 max-h-[60vh] min-h-[6rem] overflow-y-auto whitespace-pre-wrap font-mono text-[12px] leading-[18px]">
        {busy && <span className="text-text-subtle">…</span>}
        {error && <span className="text-[var(--color-danger)]">{error}</span>}
        {!busy && !error && result && result}
      </div>
      <div className="mt-2 flex gap-2 text-[11px]">
        <button
          onClick={onRetry}
          disabled={busy}
          className="btn"
        >
          Retry
        </button>
        {onAccept && result && !busy && (
          <button onClick={() => onAccept(result)} className="btn btn-primary">
            Accept
          </button>
        )}
      </div>
    </div>
  );
}
